// Authentication utility functions

export const persistAuthSession = (sessionToken, userInfo) => {
  localStorage.setItem('session_token', sessionToken);
  localStorage.setItem('user_info', JSON.stringify(userInfo));
};

export const clearAuthSession = () => {
  localStorage.removeItem('session_token');
  localStorage.removeItem('user_info');
};

/**
 * Get session token from localStorage
 */
export const getSessionToken = () => {
  return localStorage.getItem('session_token');
};

/**
 * Get user info from localStorage
 */
export const getUserInfo = () => {
  const userInfo = localStorage.getItem('user_info');
  return userInfo ? JSON.parse(userInfo) : null;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const token = getSessionToken();
  const userInfo = getUserInfo();
  return !!(token && userInfo);
};

/**
 * Logout user - clear session data
 */
export const logout = async () => {
  const token = getSessionToken();
  
  // Clear localStorage first
  clearAuthSession();
  
  // Call backend logout if token exists
  if (token) {
    try {
      const formData = new FormData();
      formData.append('session_token', token);
      
      await fetch('/auth/logout', {
        method: 'POST',
        body: formData,
      });
    } catch (error) {
      console.warn('Failed to call backend logout:', error);
      // Don't throw error - local logout is more important
    }
  }
  
  // Reload page to reset app state
  window.location.reload();
};

/**
 * Validate current session with backend
 */
export const validateSession = async () => {
  const token = getSessionToken();
  if (!token) return false;

  try {
    const response = await fetch('/auth/validate', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.status === 'valid' && data.user_info) {
        persistAuthSession(token, data.user_info);
      }
      return data.status === 'valid';
    } else {
      // Session invalid, clear local storage
      clearAuthSession();
      return false;
    }
  } catch (error) {
    console.error('Session validation error:', error);
    return false;
  }
};

/**
 * Get authentication headers for API calls
 */
export const getAuthHeaders = () => {
  const token = getSessionToken();
  if (!token) return {};

  return {
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Make authenticated API call
 */
export const authenticatedFetch = async (url, options = {}) => {
  const token = getSessionToken();
  
  if (!token) {
    throw new Error('No session token available');
  }

  const authOptions = {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    }
  };

  const response = await fetch(url, authOptions);

  // If unauthorized, clear session and redirect to login
  if (response.status === 401) {
    clearAuthSession();
    window.location.reload();
    throw new Error('Session expired');
  }

  return response;
};

/**
 * Auto-refresh session periodically
 */
export const startSessionRefresh = () => {
  // Validate session every 5 minutes
  const intervalId = setInterval(async () => {
    const isValid = await validateSession();
    if (!isValid && isAuthenticated()) {
      // Session expired, logout user
      await logout();
    }
  }, 5 * 60 * 1000); // 5 minutes

  return intervalId;
};

/**
 * Stop session refresh interval
 */
export const stopSessionRefresh = (intervalId) => {
  if (intervalId) {
    clearInterval(intervalId);
  }
};
